
import { Navigate, type RouteObject } from 'react-router-dom';
import { lazy } from 'react';

// Lazy load components
const HomePage = lazy(() => import('../pages/home/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const ServicesPage = lazy(() => import('../pages/services/page'));
const ServiceDetailPage = lazy(() => import('../pages/services/components/ServiceDetail'));
const GalleryPage = lazy(() => import('../pages/gallery/page'));
const TestimonialsPage = lazy(() => import('../pages/testimonials/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const HirePage = lazy(() => import('../pages/hire/page'));
const CareersPage = lazy(() => import('../pages/careers/page'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));

// Admin Components
const AdminLayout = lazy(() => import('../pages/admin/AdminLayout'));
const AdminLogin = lazy(() => import('../pages/admin/Login'));
const AdminResetPassword = lazy(() => import('../pages/admin/ResetPassword'));
import ProtectedRoute from '../components/auth/ProtectedRoute';
const Dashboard = lazy(() => import('../pages/admin/Dashboard'));
const EmployeeManagement = lazy(() => import('../pages/admin/EmployeeManagement'));
const AttendanceMonitoring = lazy(() => import('../pages/admin/AttendanceMonitoring'));
const LeaveApproval = lazy(() => import('../pages/admin/LeaveApproval'));
const PayrollManagement = lazy(() => import('../pages/admin/PayrollManagement'));
const Services = lazy(() => import('../pages/admin/Services'));
const Testimonials = lazy(() => import('../pages/admin/Testimonials'));
const Gallery = lazy(() => import('../pages/admin/Gallery'));
const CompanyInfo = lazy(() => import('../pages/admin/CompanyInfo'));
const Enquiries = lazy(() => import('../pages/admin/Enquiries'));
const Messages = lazy(() => import('../pages/admin/Messages'));
const Settings = lazy(() => import('../pages/admin/Settings'));

// Employee Pages
const EmployeeLogin = lazy(() => import('../pages/employee/EmployeeLogin'));
const EmployeeLayout = lazy(() => import('../pages/employee/EmployeeLayout'));
const EmployeeDashboard = lazy(() => import('../pages/employee/EmployeeDashboard'));
const EmployeeAttendance = lazy(() => import('../pages/employee/EmployeeAttendance'));
const EmployeeLeave = lazy(() => import('../pages/employee/EmployeeLeave'));
const EmployeeSalary = lazy(() => import('../pages/employee/EmployeeSalary'));
const EmployeeProfile = lazy(() => import('../pages/employee/EmployeeProfile'));

const WelcomePage = lazy(() => import('../pages/Welcome'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <WelcomePage />
  },
  {
    path: '/home',
    element: <HomePage />
  },
  {
    path: '/about',
    element: <AboutPage />
  },
  {
    path: '/services',
    element: <ServicesPage />
  },
  {
    path: '/services/:slug',
    element: <ServiceDetailPage />
  },
  {
    path: '/gallery',
    element: <GalleryPage />
  },
  {
    path: '/testimonials',
    element: <TestimonialsPage />
  },
  {
    path: '/contact',
    element: <ContactPage />
  },
  {
    path: '/hire',
    element: <HirePage />
  },
  {
    path: '/careers',
    element: <CareersPage />
  },
  // Admin Routes
  {
    path: '/admin',
    children: [
      {
        path: 'login',
        element: <AdminLogin />
      },
      {
        path: 'reset-password',
        element: <AdminResetPassword />
      },
      {
        element: <ProtectedRoute><AdminLayout /></ProtectedRoute>,
        children: [
          {
            index: true,
            element: <Dashboard />
          },
          {
            path: 'employees',
            element: <EmployeeManagement />
          },
          {
            path: 'attendance',
            element: <AttendanceMonitoring />
          },
          {
            path: 'leaves',
            element: <LeaveApproval />
          },
          {
            path: 'payroll',
            element: <PayrollManagement />
          },
          {
            path: 'services',
            element: <Services />
          },
          {
            path: 'testimonials',
            element: <Testimonials />
          },
          {
            path: 'gallery',
            element: <Gallery />
          },
          {
            path: 'company-info',
            element: <CompanyInfo />
          },
          {
            path: 'enquiries',
            element: <Enquiries />
          },
          {
            path: 'messages',
            element: <Messages />
          },
          {
            path: 'settings',
            element: <Settings />
          }
        ]
      }
    ]
  },
  // Employee Routes
  {
    path: '/employee/login',
    element: <EmployeeLogin />
  },
  {
    path: '/employee',
    element: <EmployeeLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/employee/dashboard" replace />
      },
      {
        path: 'dashboard',
        element: <EmployeeDashboard />
      },
      {
        path: 'attendance',
        element: <EmployeeAttendance />
      },
      {
        path: 'leave',
        element: <EmployeeLeave />
      },
      {
        path: 'salary',
        element: <EmployeeSalary />
      },
      {
        path: 'profile',
        element: <EmployeeProfile />
      }
    ]
  },
  {
    path: '*',
    element: <NotFoundPage />
  }
];

export default routes;
